import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlarmClock, CalendarCheck, ChevronLeft, ChevronRight, Flame, LogOut,
  Timer, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const MAX_MONTHS_BACK = 6; // matches the 180-day photo/data retention window

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const Tile = ({ icon: Icon, label, value, sub, tone = "default", testId }) => (
  <div data-testid={testId}
    className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5">
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
      tone === "warn" ? "bg-amber-100 text-amber-700"
        : tone === "good" ? "bg-emerald-100 text-emerald-700"
          : "bg-primary/10 text-primary"}`}>
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <p className="text-xl font-semibold tabular-nums leading-tight">{value}</p>
      <p className="mt-0.5 text-xs font-medium text-muted-foreground">{label}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

/* Staff self-service punctuality summary (Batch 8). Own data only — the
   backend derives the user from the token. Includes an on-time streak so
   staff are nudged to self-correct lateness. */
export default function StaffSummaryPage() {
  const { t, lang } = useLang();
  const now = new Date();
  const [offset, setOffset] = useState(0); // 0 = current month, -1 = previous…

  const viewDate = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const month = monthKey(viewDate);
  const monthLabel = viewDate.toLocaleDateString(
    lang === "ms" ? "ms-MY" : "en-MY", { month: "long", year: "numeric" });

  const { data, isLoading } = useQuery({
    queryKey: ["my-summary", month],
    queryFn: async () => (await api.get("/punches/me/summary", { params: { month } })).data,
  });

  const streak = data?.onTimeStreak ?? 0;

  return (
    <AppShell>
      <div className="mx-auto max-w-md space-y-4" data-testid="staff-summary-page">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold tracking-tight">{t("mySummary")}</h1>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8"
              data-testid="summary-prev-month"
              disabled={offset <= -MAX_MONTHS_BACK}
              onClick={() => setOffset((o) => o - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span data-testid="summary-month-label"
              className="min-w-[8.5rem] text-center text-sm font-medium">
              {monthLabel}
            </span>
            <Button size="icon" variant="outline" className="h-8 w-8"
              data-testid="summary-next-month"
              disabled={offset >= 0}
              onClick={() => setOffset((o) => o + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
          </div>
        ) : data && (
          <>
            {/* streak motivator */}
            <Card data-testid="streak-card"
              className={`shadow-sm ${streak > 0 ? "border-emerald-200 bg-emerald-50/60" : ""}`}>
              <CardContent className="flex items-center gap-4 pt-5">
                <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
                  streak > 0 ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                  <Flame className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("onTimeStreakLabel")}</p>
                  <p className="text-2xl font-semibold leading-tight tabular-nums" data-testid="streak-value">
                    {streak} <span className="text-sm font-normal text-muted-foreground">{t("streakUnit")}</span>
                  </p>
                  <p className={`mt-0.5 text-xs ${streak > 0 ? "text-emerald-700" : "text-muted-foreground"}`}
                    data-testid="streak-message">
                    {streak > 0 ? t("streakPraise") : t("streakStart")}
                  </p>
                </div>
              </CardContent>
            </Card>

            {data.daysWorked === 0 && data.days.length === 0 ? (
              <p data-testid="summary-empty"
                className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                {t("noSummaryMonth")}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Tile icon={CalendarCheck} label={t("daysWorked")}
                    value={data.daysWorked} testId="tile-days-worked" />
                  <Tile icon={TrendingUp} label={t("onTimeRate")}
                    value={data.onTimeRate != null ? `${data.onTimeRate}%` : "–"}
                    tone={data.onTimeRate >= 90 ? "good" : "default"}
                    testId="tile-on-time-rate" />
                  <Tile icon={AlarmClock} label={t("lateDaysLabel")}
                    value={data.lateDays}
                    sub={data.lateDays > 0 ? `${data.totalMinutesLate} ${t("totalMinLate")}` : null}
                    tone={data.lateDays > 0 ? "warn" : "good"}
                    testId="tile-late-days" />
                  <Tile icon={Timer} label={t("avgMinutesLateLabel")}
                    value={data.avgMinutesLate ?? "–"}
                    tone={data.avgMinutesLate ? "warn" : "default"}
                    testId="tile-avg-late" />
                  <Tile icon={LogOut} label={t("earlyDeparturesLabel")}
                    value={data.earlyDepartures}
                    sub={data.earlyDepartures > 0 ? `${data.totalMinutesEarly} ${t("totalMinEarly")}` : null}
                    tone={data.earlyDepartures > 0 ? "warn" : "default"}
                    testId="tile-early-departures" />
                  <Tile icon={CalendarCheck} label={t("onTimeDaysLabel")}
                    value={data.onTimeDays} tone="good" testId="tile-on-time-days" />
                </div>

                {/* day-by-day strip */}
                {data.days.length > 0 && (
                  <Card className="shadow-sm" data-testid="day-by-day-card">
                    <CardContent className="space-y-3 pt-4">
                      <p className="text-sm font-semibold">{t("dayByDay")}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {data.days.map((d) => {
                          const dayNum = Number(d.date.slice(-2));
                          return (
                            <span key={d.date} data-testid="day-chip"
                              title={d.minutesLate ? `+${d.minutesLate} ${t("minLate")}` : t("legendOnTime")}
                              className={`flex h-9 min-w-9 flex-col items-center justify-center rounded-lg px-1 text-[11px] font-semibold tabular-nums ${
                                d.onTime ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
                              {dayNum}
                              {d.minutesLate ? <span className="text-[9px] font-medium">+{d.minutesLate}m</span> : null}
                            </span>
                          );
                        })}
                      </div>
                      <div className="flex gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-300" /> {t("legendOnTime")}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="h-2.5 w-2.5 rounded-sm bg-amber-300" /> {t("legendLate")}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
